import React from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Card, DniInput, EyeIcon, Input, Select } from '@avila-tek/ui/src';
import { User, UserTypeEnum } from '../../../../models';
import { ACTIONS, ACTIONS_TYPE } from './reducer';
import { dniTypes, roles } from '../../../../static';
import FormHeader, { FormHeaderProps } from '../FormHeader';

interface GeneralUserFormProps {
  userType: UserTypeEnum;
  dispatch?: React.Dispatch<ACTIONS_TYPE>;
  state?: Partial<User>;
  header?: FormHeaderProps;
  disabled?: boolean;
  requiredUserDni?: boolean;
}

function GeneralUserForm({
  header,
  state = {},
  dispatch = () => {},
  userType = roles.admin,
  disabled = false,
  requiredUserDni = true,
}: GeneralUserFormProps) {
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatch({ type: name as any, payload: value });
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    dispatch({ type: name as any, payload: value });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setError(validatePassword(newPassword));
    handleChange(e);
  };

  const validatePassword = (passwordItem: string) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])[A-Za-z\d\W_]{8,}$/;

    if (passwordItem.length < 12) {
      return 'La contraseña debe contener al menos 12 caracteres';
    }
    if (!passwordRegex.test(passwordItem)) {
      return 'La contraseña debe contener al menos una letra mayúscula, una letra minúscula, un número y un carácter especial';
    }

    return '';
  };

  return (
    <Card>
      <FormHeader
        href={header?.href ?? ''}
        backBtnText={header?.backBtnText ?? ''}
        title={header?.title ?? ''}
      />
      <Card.Body>
        <div className="w-full flex md:flex-row flex-col gap-5">
          <div className="w-full">
            <Input
              name={ACTIONS.FIRST_NAME}
              value={state?.firstName ?? ''}
              label="Nombre"
              type="text"
              placeholder="Ingrese un nombre"
              onChange={handleChange}
              required
            />
          </div>
          <div className="w-full">
            <Input
              name={ACTIONS.LAST_NAME}
              value={state?.lastName ?? ''}
              label="Apellido"
              type="text"
              placeholder="Ingrese un apellido"
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div className=" flex md:flex-row flex-col gap-5">
          {requiredUserDni ? (
            <div className="w-full">
              <DniInput
                label="Cédula de identidad"
                defaultSelect={dniTypes[0].value}
                selectName={ACTIONS.DNI_TYPE}
                inputName={ACTIONS.DNI}
                selectValue={state?.dniType}
                inputValue={state?.dni ?? ''}
                handleChangeSelect={handleChangeSelect}
                handleChange={handleChange}
                placeholder="Ingrese una cédula de identidad"
              >
                {dniTypes.map((option) => (
                  <option value={option.name} key={option.name}>
                    {option.name}
                  </option>
                ))}
              </DniInput>
            </div>
          ) : null}
          <div className="w-full">
            <Input
              name={ACTIONS.EMAIL}
              value={state?.email ?? ''}
              label="Correo electrónico"
              type="email"
              placeholder="Ingrese un correo electrónico"
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div className=" flex md:flex-row flex-col gap-5">
          <div className="w-full">
            <span className="text-gray-300 text-sm font-arial font-extrabold mb-2 tracking-wider  ">
              Teléfono
            </span>
            <PhoneInput
              inputStyle={{
                appearance: 'none',
                height: '32px',
                background: 'transparent',
                width: '100%',
                border: 'solid',
                borderWidth: '0.5px',
                borderColor: '#A6A6A6',
                borderRadius: '4px',
                minHeight: '40px',
                fontSize: '16px',
                color: '#606060',
                fontWeight: '300',
              }}
              countryCodeEditable={false}
              country="ve"
              value={state?.phone}
              placeholder="Ingrese un teléfono"
              onChange={(phone) => {
                dispatch({ type: ACTIONS.PHONE, payload: phone });
              }}
              defaultMask="... ... ...."
            />
          </div>
          {!state?._id ? (
            <div className="w-full">
              <div className="">
                <Input
                  label="Contraseña"
                  placeholder="********"
                  name={ACTIONS.PASSWORD}
                  onChange={handlePasswordChange}
                  type={showPassword ? 'text' : 'password'}
                  rightIcon={
                    <EyeIcon
                      className="w-5 h-5 cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPassword(!showPassword);
                      }}
                    />
                  }
                />
              </div>
              {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
            </div>
          ) : null}
        </div>
        {userType === roles.admin ? (
          <div className=" flex md:flex-row flex-col gap-5">
            <div className="w-full md:w-1/2">
              <Select
                name={ACTIONS.PRIVILEGE}
                value={state.privilege ?? ''}
                label="Privilegio"
                onChange={handleChangeSelect}
                required
              >
                <option value="0">Admin</option>
              </Select>
            </div>
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}

export default GeneralUserForm;
